import {
  createResource,
  deleteResource,
  getResource,
  readUrl,
  throwOnApiError,
  updateResource,
  updateResourceBodyless,
} from "./api";

// Same as quotes.js: the Featured In section pages through every row on the client, so it reads them
// all, up to the model's cap (Id::MediaItem::MAX_ASSOCIATIONS_ALLOWED), and pins the order explicitly
// rather than trusting the view's own `order by list_position` to survive PostgREST's wrapping query.
const ROWS_LIMIT = 250;

// Read: PostgREST, scoped to one polymorphic owner. `ownerType` must be the CONCRETE STI owner_type the
// rows are persisted with (e.g. "Id::PromoEntity::Expert"), never the abstract "Id::PromoEntity" --
// Rails' polymorphic_name serializes the leaf class, so a query against the base name matches nothing.
const getAllByOwner = async (ownerType, ownerId) => {
  const selector = `owner_id=eq.${ownerId}&owner_type=eq.${ownerType}&order=list_position.asc`;
  const url = readUrl("media_items", { selector, paginationParams: `limit=${ROWS_LIMIT}` });
  return getResource(url);
};

// Mutations go through the nested Rails routes (Id::Api::MediaItemsController) -- writes never go
// through PostgREST/Supabase-js. `resource` is the route segment for the owning entity type ("experts",
// "companies", "products" -- see Id::Engine routes' quotable_and_media_itemable concern), kept generic
// so companies/products can adopt this client later; only the Expert profile page wires editing today.
const createForOwner = async (resource, ownerId, attributes) => {
  const url = `/id/api/${resource}/${ownerId}/media_items`;
  // Check for `{ error }` BEFORE unwrapping to attributes -- otherwise a failed create returns
  // `undefined` and the caller can't tell success from failure.
  const response = throwOnApiError(await createResource(url, { data: { attributes } }));
  return response?.data?.data?.attributes;
};

const updateById = async (resource, ownerId, id, attributes) => {
  const url = `/id/api/${resource}/${ownerId}/media_items/${id}`;
  return throwOnApiError(await updateResource(url, { data: { attributes } }));
};

const deleteById = async (resource, ownerId, id) => {
  const url = `/id/api/${resource}/${ownerId}/media_items/${id}`;
  return throwOnApiError(await deleteResource(url));
};

// list_position is the ABSOLUTE target position (1-indexed) within the owner's list, not a delta --
// the server (acts_as_list) renumbers every sibling row that needs to shift, so the caller only has to
// send where this row should land (e.g. its current position -1 for "move up").
const updateListPositionById = async (resource, ownerId, id, listPosition) => {
  const url = `/id/api/${resource}/${ownerId}/media_items/${id}/update_list_position`;
  return throwOnApiError(await updateResource(url, { data: { attributes: { list_position: listPosition } } }));
};

// feature/unfeature are a bodyless PUT pair (FlippableRoute), not a single toggle endpoint -- the
// caller's `featured` boolean selects which of the two routes to call.
const setFeaturedById = async (resource, ownerId, id, featured) => {
  const action = featured ? "feature" : "unfeature";
  const url = `/id/api/${resource}/${ownerId}/media_items/${id}/${action}`;
  return throwOnApiError(await updateResourceBodyless(url));
};

export default { createForOwner, deleteById, getAllByOwner, setFeaturedById, updateById, updateListPositionById };
